// Threat Insights — drill-down drawer.
//
// Renders one `threat_insight_findings` row using the v3 event drawer shell
// and shared SectionCard / EventAssessGrid components.

import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import copy from "copy-to-clipboard";
import { Clock, Globe, Hash, User, CheckCircle, FileText, EyeOff, BellOff, Undo2 } from "lucide-react";

import useGlobalSite from "../../../../hooks/user/useGlobalSite";
import threatInsights from "../../../../utils/axios/v1/threatInsightsAxios";
import colors from "../../../../utils/colors";
import { SafeTextField } from "../../../common/Greens";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import WafEventDrawerShell from "../detection/WafEventDrawerShell";
import SmartParseDrawerHeader from "../detection/v3/SmartParseDrawerHeader";
import SmartParseRawJsonViewer from "../detection/v3/SmartParseRawJsonViewer";
import { EventAssessGrid, DrawerFieldStack, SmartParseSectionCard as SectionCard, spDrawerFooterSx, spSecondaryBodySx } from "../detection/v3/smartParseDrawerUi";
import {
  PRIMARY_COLOR,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BORDER_COLOR,
  tagChipSx,
  secondaryButtonStyle,
} from "../detection/wafEventDrawerShared";
import {
  buildThreatInsightReferences,
  buildThreatContextFields,
  buildWhyFiredMatchFields,
  buildWhyFiredRuleFields,
  formatSampleSummaryLine,
  getEvidenceSampleSubtitle,
  getEvidenceTruncationMessage,
} from "./threatInsightDrawerData";
import SilenceThreatForm from "./SilenceThreatForm";
import UndoSuppressForm from "./UndoSuppressForm";

const SEVERITY_PALETTE = {
  critical: { label: "Critical", bg: "rgba(251,55,72,0.10)", fg: colors.red[200], border: "rgba(251,55,72,0.30)" },
  high:     { label: "High",     bg: "rgba(251,55,72,0.10)", fg: colors.red[200], border: "rgba(251,55,72,0.30)" },
  medium:   { label: "Medium",   bg: "rgba(245,158,11,0.10)", fg: "#b45309", border: "rgba(245,158,11,0.30)" },
  low:      { label: "Low",      bg: "#F3F4F6", fg: TEXT_PRIMARY, border: BORDER_COLOR },
};

const STATUS_PALETTE = {
  open:         { label: "Open",         bg: "rgba(251,55,72,0.10)", fg: colors.red[200], border: "rgba(251,55,72,0.30)" },
  acknowledged: { label: "Acknowledged", bg: "rgba(245,158,11,0.10)", fg: "#b45309", border: "rgba(245,158,11,0.30)" },
  resolved:     { label: "Resolved",     bg: "rgba(31,193,107,0.10)", fg: colors.green[300], border: "rgba(31,193,107,0.30)" },
  suppressed:   { label: "Suppressed",   bg: "#F3F4F6", fg: TEXT_SECONDARY, border: BORDER_COLOR },
};

const ACTION_PALETTE = {
  acknowledged: { fg: "#b45309", border: "rgba(180,83,9,0.45)", hoverBg: "rgba(245,158,11,0.08)" },
  suppressed:   { fg: TEXT_PRIMARY, border: colors.neutral[300], hoverBg: colors.neutral[50] },
};

const truncationBannerSx = {
  mb: 1.5,
  px: 1.5,
  py: 1,
  borderRadius: "8px",
  backgroundColor: "rgba(245,158,11,0.08)",
  border: "1px solid rgba(245,158,11,0.30)",
};

function fmtTs(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" });
}

function fmtRelative(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  const ms = Math.max(0, Date.now() - d.getTime());
  if (ms < 60_000) return `${Math.max(1, Math.floor(ms / 1000))}s ago`;
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)} min ago`;
  if (ms < 86400_000) return `${Math.floor(ms / 3600_000)} h ago`;
  return `${Math.floor(ms / 86400_000)} d ago`;
}

function validTransitions(currentStatus) {
  const s = (currentStatus || "").toLowerCase();
  if (s === "open") return ["acknowledged", "resolved", "suppressed"];
  if (s === "acknowledged") return ["resolved", "suppressed"];
  return [];
}

function canSilenceFinding(status) {
  const s = (status || "").toLowerCase();
  return s === "open" || s === "acknowledged";
}

function canUndoSuppress(status) {
  return (status || "").toLowerCase() === "suppressed";
}

function SeverityChip({ severity }) {
  const p = SEVERITY_PALETTE[(severity || "").toLowerCase()] || SEVERITY_PALETTE.medium;
  return (
    <Chip
      label={p.label}
      size="small"
      sx={{ ...tagChipSx, backgroundColor: p.bg, color: p.fg, border: `1px solid ${p.border}` }}
    />
  );
}

function StatusChip({ status }) {
  const p = STATUS_PALETTE[(status || "").toLowerCase()] || STATUS_PALETTE.open;
  return (
    <Chip
      label={p.label}
      size="small"
      sx={{ ...tagChipSx, backgroundColor: p.bg, color: p.fg, border: `1px solid ${p.border}` }}
    />
  );
}

function EvidenceTruncationBanner({ message }) {
  if (!message) return null;
  return (
    <Typography sx={{ ...truncationBannerSx, fontSize: 12.5, color: "#92400e", lineHeight: 1.45 }}>
      {message}
    </Typography>
  );
}

const ACTION_LABEL = {
  acknowledged: "Acknowledge",
  resolved: "Resolve",
  suppressed: "Suppress",
};
const ACTION_BUSY_LABEL = {
  acknowledged: "Acknowledging…",
  resolved: "Resolving…",
  suppressed: "Suppressing…",
};

const TRANSITION_ORDER = ["acknowledged", "suppressed", "resolved"];

const actionButtonBaseSx = {
  height: 36,
  borderRadius: "8px",
  fontSize: 14,
  fontWeight: 500,
  textTransform: "none",
  width: "auto",
  minWidth: 104,
  boxShadow: "none",
};

function getOutlinedActionSx(fg, border, hoverBg) {
  return {
    ...actionButtonBaseSx,
    color: fg,
    backgroundColor: "#fff",
    border: `1px solid ${border}`,
    "& fieldset": { borderColor: border },
    "&&": { color: fg, backgroundColor: "#fff", borderColor: border },
    "&&:hover": {
      color: fg,
      backgroundColor: hoverBg || colors.neutral[50],
      borderColor: fg,
      boxShadow: "none",
    },
    "&&:hover fieldset": { borderColor: fg },
    "&&.Mui-disabled": {
      color: colors.neutral[500],
      borderColor: colors.neutral[200],
      backgroundColor: colors.neutral[50],
      opacity: 1,
    },
    "&&.Mui-disabled fieldset": { borderColor: colors.neutral[200] },
  };
}

function getContainedResolveSx() {
  return {
    ...actionButtonBaseSx,
    color: "#fff",
    backgroundColor: colors.primary[300],
    border: `1px solid ${colors.primary[300]}`,
    "&:hover": {
      backgroundColor: colors.primary[400],
      borderColor: colors.primary[400],
      color: "#fff",
      boxShadow: "none",
    },
    "&:active": {
      backgroundColor: colors.primary[400],
      borderColor: colors.primary[400],
      color: "#fff",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      color: "#fff",
      backgroundColor: colors.primary[200],
      borderColor: colors.primary[200],
      opacity: 0.7,
    },
  };
}

const cancelButtonSx = {
  ...secondaryButtonStyle,
  ...actionButtonBaseSx,
  "& fieldset": { borderColor: BORDER_COLOR },
  "&:hover": {
    backgroundColor: "#fff",
    color: TEXT_PRIMARY,
    borderColor: TEXT_PRIMARY,
    boxShadow: "none",
  },
  "&:hover fieldset": { borderColor: TEXT_PRIMARY },
};

export default function ThreatInsightDrawer({
  open,
  onClose,
  finding,
  initialSilenceMode,
  onUpdated,
  onError,
  onSuccess,
}) {
  const navigate = useNavigate();
  const { setCurSiteId } = useGlobalSite();

  const [notes, setNotes] = React.useState("");
  const [busyAction, setBusyAction] = React.useState(null);
  const [silenceActive, setSilenceActive] = React.useState(false);
  const [silenceSaving, setSilenceSaving] = React.useState(false);
  const [undoActive, setUndoActive] = React.useState(false);
  const [undoSaving, setUndoSaving] = React.useState(false);
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [notesHistory, setNotesHistory] = React.useState([]);
  const [notesHistoryLoading, setNotesHistoryLoading] = React.useState(false);
  const [notesHistoryError, setNotesHistoryError] = React.useState(false);
  const [notesFilter, setNotesFilter] = React.useState("all");

  const loadNotesHistory = React.useCallback(async (findingId) => {
    if (!findingId) {
      setNotesHistory([]);
      setNotesHistoryError(false);
      return;
    }
    setNotesHistoryError(false);
    setNotesHistoryLoading(true);
    try {
      const r = await threatInsights.findingNotes(findingId);
      setNotesHistory(Array.isArray(r?.data?.notes) ? r.data.notes : []);
    } catch {
      setNotesHistory([]);
      setNotesHistoryError(true);
    } finally {
      setNotesHistoryLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setNotes("");
    setBusyAction(null);
    setSilenceSaving(false);
    setSilenceActive(Boolean(initialSilenceMode) && canSilenceFinding(finding?.status));
    setUndoActive(false);
    setUndoSaving(false);
    setSnackOpen(false);
    setNotesFilter("all");
    loadNotesHistory(finding?.id);
  }, [open, finding?.id, initialSilenceMode, finding?.status, loadNotesHistory]);

  const handleCopy = (value) => {
    if (!value) return;
    copy(String(value));
    setSnackOpen(true);
    setTimeout(() => setSnackOpen(false), 2000);
  };

  const rawFindingPayload = React.useMemo(() => {
    if (!finding) return {};
    return finding;
  }, [finding]);

  if (!finding) {
    return (
      <WafEventDrawerShell open={false} onClose={onClose} title="Threat Insight">
        {null}
      </WafEventDrawerShell>
    );
  }

  const rule = finding.rule || {};
  const ruleId = finding.rule_id || rule.id || "—";
  const ruleName = rule.name || ruleId;
  const evidence = finding.evidence || {};
  // TI-10 (2026-07-03) — a kill-chain finding carries either:
  //   * evidence.chain: [ruleId, ...] (written by runCompositePass)
  //   * rule.composite_of: [ruleId, ...] (fallback for older findings)
  // and by convention rule_id starts with `KIL-`.
  const isKillChain =
    /^KIL-/.test(ruleId) ||
    (Array.isArray(evidence.chain) && evidence.chain.length >= 2);
  const chainRuleIds =
    (Array.isArray(evidence.chain) && evidence.chain) ||
    (Array.isArray(rule.composite_of) && rule.composite_of) ||
    [];
  const samples = Array.isArray(evidence.samples) ? evidence.samples : [];
  const ips = (Array.isArray(evidence.ips) ? evidence.ips : []).filter(
    (ip) => String(ip ?? "").trim() !== ""
  );
  const matchCount = Number(evidence.match_count || 0);
  const truncationMessage = getEvidenceTruncationMessage(evidence);
  const evidenceSubtitle = getEvidenceSampleSubtitle(matchCount, samples.length);
  const references = buildThreatInsightReferences(rule);
  const whyFiredRuleFields = buildWhyFiredRuleFields(rule, finding);
  const whyFiredMatchFields = buildWhyFiredMatchFields(finding, evidence, ips, samples, fmtTs);

  const trimNonEmpty = (v) => String(v ?? "").trim() !== "";
  const hasIp = samples.some((s) => trimNonEmpty(s?.ip));
  const hasUri = samples.some((s) => trimNonEmpty(s?.uri));
  const hasRequestId = samples.some((s) => trimNonEmpty(s?.request_id));
  const subjectValue = trimNonEmpty(evidence.subject_value) ? String(evidence.subject_value).trim() : null;
  const subjectKindHint = evidence.subject_kind_hint || finding.subject_kind || null;
  const isCrossSite = (finding.rule?.scope || rule?.scope) === "global";
  const threatContextFields = buildThreatContextFields({
    finding,
    rule,
    ips,
    matchCount,
    samples,
    evidence,
    isCrossSite,
    subjectValue,
    subjectKindHint,
  });
  const transitions = validTransitions(finding.status).sort(
    (a, b) => TRANSITION_ORDER.indexOf(a) - TRANSITION_ORDER.indexOf(b)
  );

  const goToRequest = (requestId) => {
    if (!requestId) return;
    onClose && onClose();
    if (finding?.site_id) {
      setCurSiteId(finding.site_id);
    }
    navigate("/v2/detection/waf_event", {
      state: {
        initialFilters: [
          { id: "request_id", label: "Request ID", value: requestId },
        ],
      },
    });
  };

  const handleAction = async (nextStatus) => {
    if (!finding?.id || busyAction || silenceSaving || undoSaving) return;
    if (notes && notes.length > 500) {
      onError && onError("Notes must be 500 characters or fewer.");
      return;
    }
    setBusyAction(nextStatus);
    try {
      const body = { status: nextStatus };
      if (notes && notes.trim()) body.notes = notes.trim();
      await threatInsights.updateFinding(finding.id, body);
      onSuccess && onSuccess("Updated");
      onUpdated && onUpdated();
      await loadNotesHistory(finding.id);
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        (typeof err === "string" ? err : null) ||
        "Failed to update finding";
      onError && onError(msg);
    } finally {
      setBusyAction(null);
    }
  };

  const handleSilenceSubmit = async (body) => {
    if (!finding?.id || silenceSaving || busyAction || undoSaving) return;
    setSilenceSaving(true);
    try {
      await threatInsights.suppressFinding(finding.id, body);
      onSuccess && onSuccess("Rule silenced for this site.");
      onUpdated && onUpdated();
      await loadNotesHistory(finding.id);
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        (typeof err === "string" ? err : null) ||
        "Silence failed";
      onError && onError(msg);
    } finally {
      setSilenceSaving(false);
    }
  };

  const handleUndoSubmit = async (body) => {
    if (!finding?.id || undoSaving || busyAction || silenceSaving) return;
    setUndoSaving(true);
    try {
      await threatInsights.updateFinding(finding.id, {
        status: "open",
        reopen_reason: body.reopen_reason,
      });
      onSuccess && onSuccess("Finding reopened.");
      onUpdated && onUpdated();
      await loadNotesHistory(finding.id);
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        (typeof err === "string" ? err : null) ||
        "Undo failed";
      onError && onError(msg);
    } finally {
      setUndoSaving(false);
    }
  };

  const summaryCells = [
    {
      key: "rule",
      label: "Rule",
      value: `${ruleId} · ${ruleName}`,
      icon: DETECTION_ICONS.detection,
    },
    finding.site_id
      ? { key: "site", label: "Site", value: finding.site_id, icon: Globe }
      : null,
    {
      key: "first_seen",
      label: "First seen",
      value: `${fmtRelative(finding.first_seen_at)} · ${fmtTs(finding.first_seen_at)}`,
      icon: Clock,
    },
    {
      key: "last_seen",
      label: "Last seen",
      value: `${fmtRelative(finding.last_seen_at)} · ${fmtTs(finding.last_seen_at)}`,
      icon: Clock,
    },
    {
      key: "matches",
      label: "Matches",
      value: matchCount ? matchCount.toLocaleString() : "0",
      icon: Hash,
    },
    finding.acknowledged_by
      ? {
        key: "ack_by",
        label: "Acknowledged by",
        value: `${finding.acknowledged_by}${finding.acknowledged_at ? ` · ${fmtTs(finding.acknowledged_at)}` : ""}`,
        icon: User,
      }
      : null,
    finding.resolved_at
      ? {
        key: "resolved",
        label: (finding.status || "").toLowerCase() === "suppressed" ? "Suppressed at" : "Resolved at",
        value: fmtTs(finding.resolved_at),
        icon: CheckCircle,
      }
      : null,
  ].filter(Boolean);

  const showSilenceAction = canSilenceFinding(finding.status);
  const showUndoAction = canUndoSuppress(finding.status);
  const workflowBusy = Boolean(busyAction) || silenceSaving || undoSaving;
  const displayNotesHistory = notesHistory.filter((row) => {
    if (notesFilter === "all") return true;
    const actionKey = String(row.action || "").toLowerCase();
    if (notesFilter === "suppress") return actionKey === "suppressed";
    return actionKey === notesFilter;
  });
  const notesFilterOptions = [
    { value: "all", label: "All" },
    { value: "undo", label: "Undo" },
    { value: "suppress", label: "Suppress" },
    { value: "silence", label: "Silence" },
  ];

  const workflowFooter = (
    <Box sx={{ ...spDrawerFooterSx, background: colors.neutral[50], backgroundColor: colors.neutral[50] }}>
      {silenceActive && showSilenceAction ? (
        <SilenceThreatForm
          finding={finding}
          saving={silenceSaving}
          onCancel={() => setSilenceActive(false)}
          onSubmit={handleSilenceSubmit}
        />
      ) : undoActive && showUndoAction ? (
        <UndoSuppressForm
          saving={undoSaving}
          onCancel={() => setUndoActive(false)}
          onSubmit={handleUndoSubmit}
        />
      ) : (
        <Stack spacing={1.5} sx={{ width: "100%" }}>
          {transitions.length > 0 ? (
            <SafeTextField
              multiline
              rows={2}
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optional, max 500 chars)…"
              inputProps={{ maxLength: 500 }}
              sx={{
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": { fontSize: 13 },
              }}
            />
          ) : (
            <Stack spacing={0.5}>
              <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY, fontStyle: "italic" }}>
                {showUndoAction
                  ? "This finding is suppressed — undo to reopen it, or leave it closed."
                  : `This finding is ${finding.status?.toLowerCase() || "closed"} — no further transitions are available.`}
              </Typography>
              {finding.resolved_at && (
                <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY }}>
                  {(finding.status || "").toLowerCase() === "suppressed" ? "Suppressed" : "Closed"}{" "}
                  {fmtRelative(finding.resolved_at)} · {fmtTs(finding.resolved_at)}
                </Typography>
              )}
            </Stack>
          )}

          <Stack direction="row" spacing={1.5} justifyContent="flex-end" alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
            <Button
              variant="outlined"
              disableElevation
              onClick={onClose}
              disabled={workflowBusy}
              sx={cancelButtonSx}
            >
              Cancel
            </Button>
            {showSilenceAction && (
              <Button
                variant="outlined"
                color="inherit"
                disableElevation
                onClick={() => setSilenceActive(true)}
                disabled={workflowBusy}
                sx={getOutlinedActionSx(
                  ACTION_PALETTE.suppressed.fg,
                  ACTION_PALETTE.suppressed.border,
                  ACTION_PALETTE.suppressed.hoverBg
                )}
              >
                Silence
              </Button>
            )}
            {showUndoAction && (
              <Button
                variant="outlined"
                color="inherit"
                disableElevation
                onClick={() => setUndoActive(true)}
                disabled={workflowBusy}
                sx={getOutlinedActionSx(
                  colors.primary[300],
                  colors.primary[200],
                  "rgba(59, 130, 246, 0.06)"
                )}
              >
                Undo
              </Button>
            )}
            {transitions.map((nextStatus) => {
              const isBusy = busyAction === nextStatus;
              const label = isBusy
                ? (ACTION_BUSY_LABEL[nextStatus] || "Working…")
                : (ACTION_LABEL[nextStatus] || nextStatus);
              const actionPalette = ACTION_PALETTE[nextStatus];

              if (nextStatus === "resolved") {
                return (
                  <Button
                    key={nextStatus}
                    variant="contained"
                    disableElevation
                    onClick={() => handleAction(nextStatus)}
                    disabled={workflowBusy}
                    startIcon={isBusy ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
                    sx={getContainedResolveSx()}
                  >
                    {label}
                  </Button>
                );
              }

              return (
                <Button
                  key={nextStatus}
                  variant="outlined"
                  color="inherit"
                  disableElevation
                  onClick={() => handleAction(nextStatus)}
                  disabled={workflowBusy}
                  startIcon={isBusy ? <CircularProgress size={14} sx={{ color: actionPalette.fg }} /> : null}
                  sx={getOutlinedActionSx(actionPalette.fg, actionPalette.border, actionPalette.hoverBg)}
                >
                  {label}
                </Button>
              );
            })}
          </Stack>
        </Stack>
      )}
    </Box>
  );

  return (
    <>
    <WafEventDrawerShell
      open={open}
      onClose={onClose}
      title={ruleName}
      headerVariant="hero"
      headerContent={(
        <SmartParseDrawerHeader
          embedded
          title="Threat Insight"
          method={ruleId !== "—" ? ruleId : null}
          path={ruleName}
          eventId={finding.id ? String(finding.id) : null}
          onCopyEventId={handleCopy}
        />
      )}
      customFooter={workflowFooter}
    >
      <SectionCard
        icon={DETECTION_ICONS.detection}
        title="Event Summary"
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
          <SeverityChip severity={finding.severity || rule.severity} />
          <StatusChip status={finding.status} />
          {rule.category && (
            <Chip
              label={rule.category}
              size="small"
              sx={{ ...tagChipSx, backgroundColor: "#F3F4F6", color: TEXT_PRIMARY }}
            />
          )}
          {isKillChain && (
            <Chip
              label="kill chain"
              size="small"
              sx={{ ...tagChipSx, backgroundColor: "#FEECEE", color: "#A11228" }}
            />
          )}
        </Stack>
        <EventAssessGrid cells={summaryCells} />

        {(notesHistoryLoading || notesHistoryError || notesHistory.length > 0) && (
          <Box
            sx={{
              mt: 2.5,
              pt: 2,
              borderTop: `1px solid ${BORDER_COLOR}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  aria-hidden
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(99,113,247,0.08)",
                    border: "1px solid rgba(99,113,247,0.20)",
                    color: PRIMARY_COLOR,
                    flexShrink: 0,
                  }}
                >
                  <FileText size={14} aria-hidden />
                </Box>
                <Typography
                  component="h3"
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: TEXT_PRIMARY,
                    letterSpacing: "-0.01em",
                  }}
                >
                  Suppress & silence notes
                </Typography>
                {notesHistory.length > 0 && (
                  <Box
                    component="span"
                    sx={{
                      minWidth: 22,
                      height: 22,
                      px: 0.75,
                      borderRadius: "999px",
                      backgroundColor: colors.neutral[100],
                      color: TEXT_SECONDARY,
                      fontSize: 11,
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {displayNotesHistory.length}
                  </Box>
                )}
              </Stack>

              {notesHistory.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {notesFilterOptions.map((opt) => {
                    const active = notesFilter === opt.value;
                    return (
                      <Button
                        key={opt.value}
                        size="small"
                        disableElevation
                        disableRipple
                        onClick={() => setNotesFilter(opt.value)}
                        sx={{
                          minWidth: 0,
                          height: 28,
                          px: 1.25,
                          borderRadius: "8px",
                          textTransform: "none",
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: 1,
                          border: `1px solid ${active ? PRIMARY_COLOR : BORDER_COLOR}`,
                          backgroundColor: active ? PRIMARY_COLOR : "#fff",
                          color: active ? "#fff" : TEXT_PRIMARY,
                          boxShadow: "none",
                          transition: "background-color 120ms ease, border-color 120ms ease, color 120ms ease",
                          "&:hover": {
                            backgroundColor: active ? "#5563E8" : "rgba(99,113,247,0.08)",
                            borderColor: PRIMARY_COLOR,
                            color: active ? "#fff" : PRIMARY_COLOR,
                            boxShadow: "none",
                          },
                          "&:active": {
                            backgroundColor: active ? "#4A58D6" : "rgba(99,113,247,0.14)",
                            borderColor: PRIMARY_COLOR,
                          },
                        }}
                      >
                        {opt.label}
                      </Button>
                    );
                  })}
                </Stack>
              )}
            </Stack>

            {notesHistoryLoading && notesHistory.length === 0 ? (
              <Typography sx={spSecondaryBodySx} aria-busy="true">
                Loading notes…
              </Typography>
            ) : notesHistoryError && notesHistory.length === 0 ? (
              <Box
                sx={{
                  px: 1.5,
                  py: 1.75,
                  borderRadius: "8px",
                  backgroundColor: colors.neutral[50],
                  border: `1px dashed ${BORDER_COLOR}`,
                }}
              >
                <Typography sx={{ ...spSecondaryBodySx, mb: 0 }}>
                  Couldn’t load notes history. Close and reopen the drawer to retry.
                </Typography>
              </Box>
            ) : displayNotesHistory.length === 0 ? (
              <Box
                sx={{
                  px: 1.5,
                  py: 1.75,
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  border: `1px solid ${BORDER_COLOR}`,
                }}
              >
                <Typography sx={{ ...spSecondaryBodySx, mb: 0 }}>
                  No {notesFilter === "all" ? "notes" : notesFilter} entries.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  overflow: "hidden",
                }}
              >
                {displayNotesHistory.map((row, idx) => {
                  const actionKey = String(row.action || "").toLowerCase();
                  const isSilence = actionKey === "silence";
                  const isUndo = actionKey === "undo";
                  const isSuppress = actionKey === "suppressed";
                  const actionLabel = isSilence
                    ? "Silence"
                    : isUndo
                      ? "Undo"
                      : isSuppress
                        ? "Suppress"
                        : (row.action || "Note");
                  const ActionIcon = isUndo ? Undo2 : isSilence ? BellOff : EyeOff;
                  const iconWrap = isUndo
                    ? {
                      backgroundColor: "rgba(59, 130, 246, 0.12)",
                      color: "#2563EB",
                    }
                    : isSilence
                      ? {
                        backgroundColor: "rgba(99,113,247,0.12)",
                        color: PRIMARY_COLOR,
                      }
                      : {
                        backgroundColor: "#F3EDE4",
                        color: "#6B4F2A",
                      };
                  const chipSx = isUndo
                    ? { backgroundColor: "rgba(59, 130, 246, 0.12)", color: "#2563EB" }
                    : isSilence
                      ? { backgroundColor: "rgba(99,113,247,0.12)", color: PRIMARY_COLOR }
                      : { backgroundColor: "#F3EDE4", color: "#6B4F2A" };
                  return (
                    <Box
                      key={row.id}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.25,
                        px: 1.5,
                        py: 1.35,
                        borderTop: idx === 0 ? "none" : `1px solid ${BORDER_COLOR}`,
                      }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.15,
                          ...iconWrap,
                        }}
                      >
                        <ActionIcon size={15} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 0.45, flexWrap: "wrap", gap: 0.75 }}
                        >
                          <Chip
                            size="small"
                            label={actionLabel}
                            sx={{
                              ...tagChipSx,
                              ...chipSx,
                              height: 22,
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          />
                          <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
                            {row.created_by || "system"}
                            {row.created_at
                              ? ` · ${fmtRelative(row.created_at)} · ${fmtTs(row.created_at)}`
                              : ""}
                          </Typography>
                        </Stack>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: TEXT_PRIMARY,
                            whiteSpace: "pre-wrap",
                            lineHeight: 1.45,
                            wordBreak: "break-word",
                          }}
                        >
                          {(row.body && String(row.body).trim()) || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </SectionCard>

      {/* TI-10 (2026-07-03) — kill-chain narrative. Renders the ordered
          list of child rule IDs the composite fired on. If we have
          chain_samples with timestamps, show the elapsed time between
          steps — that's the operational signal (fast chain = automation;
          slow chain = human). */}
      {isKillChain && chainRuleIds.length >= 2 && (
        <SectionCard icon={DETECTION_ICONS.match} title="Attack narrative">
          <Typography sx={{ ...spSecondaryBodySx, mb: 2 }}>
            This composite fired because every step below matched for the
            same subject inside {rule.window_minutes || "the rule's"} minutes.
            Steps are ordered by first-seen timestamp when available.
          </Typography>
          <Box sx={{ pl: 0.5 }}>
            {chainRuleIds.map((childId, idx) => {
              const chainSamples = (samples[0] && samples[0].chain_samples) || evidence.chain_samples || [];
              const step = chainSamples.find((s) => s.rule_id === childId);
              return (
                <Box key={`${childId}-${idx}`} sx={{ position: "relative", pl: 4, pb: idx === chainRuleIds.length - 1 ? 0 : 2 }}>
                  {/* Connector line between steps */}
                  {idx < chainRuleIds.length - 1 && (
                    <Box sx={{
                      position: "absolute",
                      left: 11,
                      top: 24,
                      bottom: -8,
                      width: 2,
                      background: "#E5E5E5",
                    }} />
                  )}
                  {/* Step index bubble */}
                  <Box sx={{
                    position: "absolute",
                    left: 0,
                    top: 4,
                    width: 24, height: 24,
                    borderRadius: "50%",
                    background: idx === chainRuleIds.length - 1 ? "#A11228" : PRIMARY_COLOR,
                    color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700,
                  }}>{idx + 1}</Box>
                  <Box sx={{
                    border: `1px solid ${BORDER_COLOR}`,
                    borderRadius: "8px",
                    p: 1.25,
                    background: "#fff",
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "monospace" }}>
                        {childId}
                      </Typography>
                      {idx === 0 && (
                        <Chip label="initial" size="small" sx={{ height: 18, fontSize: 10, background: "#EDF3FE", color: "#0244BE" }} />
                      )}
                      {idx === chainRuleIds.length - 1 && (
                        <Chip label="terminal" size="small" sx={{ height: 18, fontSize: 10, background: "#FEECEE", color: "#A11228" }} />
                      )}
                    </Stack>
                    {step && step.ts && (
                      <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
                        Fired at {fmtTs(step.ts)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </SectionCard>
      )}

      <SectionCard icon={DETECTION_ICONS.info} title="Why this fired">
        <Stack spacing={2}>
          {rule.description ? (
            <Typography sx={spSecondaryBodySx}>{rule.description}</Typography>
          ) : (
            <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, fontStyle: "italic" }}>
              No description shipped with this rule.
            </Typography>
          )}
          {whyFiredRuleFields.length > 0 && (
            <DrawerFieldStack columns={2} fields={whyFiredRuleFields} onCopy={handleCopy} />
          )}
          {whyFiredMatchFields.length > 0 && (
            <DrawerFieldStack columns={2} fields={whyFiredMatchFields} />
          )}
        </Stack>
      </SectionCard>

      <SectionCard icon={DETECTION_ICONS.match} title="Recommended action">
        {rule.remediation ? (
          <Typography sx={{ ...spSecondaryBodySx, whiteSpace: "pre-wrap" }}>{rule.remediation}</Typography>
        ) : (
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, fontStyle: "italic" }}>
            No remediation playbook shipped with this rule yet.
          </Typography>
        )}
      </SectionCard>

      <SectionCard icon={DETECTION_ICONS.request} title="Evidence">
        <EvidenceTruncationBanner message={truncationMessage} />
        {evidenceSubtitle && (
          <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY, mb: 1.5 }}>
            {evidenceSubtitle}
          </Typography>
        )}
        {samples.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, fontStyle: "italic" }}>
            {isCrossSite
              ? "This cross-site rule fired without per-request samples — see rule description for the aggregate pattern."
              : "No representative samples on this finding."}
          </Typography>
        ) : (
          <Box sx={{ border: `1px solid ${BORDER_COLOR}`, borderRadius: "8px", overflow: "hidden" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#FAFAFA" }}>
                <TableRow>
                  <TableCell sx={{ fontSize: 12, color: TEXT_SECONDARY, fontWeight: 500 }}>Timestamp</TableCell>
                  {hasIp && <TableCell sx={{ fontSize: 12, color: TEXT_SECONDARY, fontWeight: 500 }}>IP</TableCell>}
                  {hasUri && <TableCell sx={{ fontSize: 12, color: TEXT_SECONDARY, fontWeight: 500 }}>URI</TableCell>}
                  {hasRequestId && <TableCell sx={{ fontSize: 12, color: TEXT_SECONDARY, fontWeight: 500 }}>Request ID</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {samples.map((s, i) => (
                  <TableRow key={s.request_id || `${s.ts || "ts"}_${i}`}>
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY }}>{fmtTs(s.ts)}</TableCell>
                    {hasIp && (
                      <TableCell sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12, color: TEXT_PRIMARY }}>
                        {s.ip || "—"}
                      </TableCell>
                    )}
                    {hasUri && (
                      <TableCell sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12, color: TEXT_PRIMARY, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.uri || "—"}
                      </TableCell>
                    )}
                    {hasRequestId && (
                      <TableCell>
                        {s.request_id ? (
                          <Tooltip title={s.request_id} placement="top-start">
                            <Typography
                              component="button"
                              onClick={() => goToRequest(s.request_id)}
                              sx={{
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                fontSize: 12,
                                color: PRIMARY_COLOR,
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                                textAlign: "left",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {s.request_id}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>—</Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </SectionCard>

      <SectionCard
        icon={DETECTION_ICONS.info}
        title="Raw Events"
        count={samples.length}
        defaultExpanded={false}
      >
        <EvidenceTruncationBanner message={truncationMessage} />
        {samples.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, fontStyle: "italic" }}>
            No raw event samples on this finding.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {samples.map((s, i) => (
              <SectionCard
                key={s.request_id || `${s.ts || "ts"}_${i}`}
                icon={DETECTION_ICONS.request}
                title={formatSampleSummaryLine(s, fmtTs)}
                defaultExpanded={false}
              >
                <Stack spacing={1.5}>
                  {s.request_id && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => goToRequest(s.request_id)}
                      sx={{
                        ...secondaryButtonStyle,
                        alignSelf: "flex-start",
                        height: 32,
                        fontSize: 12,
                      }}
                    >
                      View in WAF Events
                    </Button>
                  )}
                  <SmartParseRawJsonViewer
                    data={s}
                    onCopy={handleCopy}
                    filename={`event_${i + 1}.json`}
                  />
                </Stack>
              </SectionCard>
            ))}
          </Stack>
        )}
      </SectionCard>

      {threatContextFields.some((f) => f.value != null && String(f.value).length > 0) && (
        <SectionCard icon={DETECTION_ICONS.ip} title="Threat Context">
          <DrawerFieldStack columns={2} fields={threatContextFields} onCopy={handleCopy} />
        </SectionCard>
      )}

      {references.length > 0 && (
        <SectionCard icon={DETECTION_ICONS.info} title="References">
          <Stack spacing={1.25}>
            {references.map((ref) => (
              <Stack key={ref.href} spacing={0.25}>
                <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY, fontWeight: 500 }}>
                  {ref.label}
                </Typography>
                <Typography
                  component="a"
                  href={ref.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: 13,
                    color: PRIMARY_COLOR,
                    textDecoration: "none",
                    wordBreak: "break-all",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {ref.href} ↗
                </Typography>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      )}

      <SectionCard icon={DETECTION_ICONS.info} title="Raw Event" defaultExpanded={false}>
        <SmartParseRawJsonViewer
          data={rawFindingPayload}
          onCopy={handleCopy}
          filename="threat_insight_finding.json"
        />
      </SectionCard>
    </WafEventDrawerShell>

    {snackOpen && (
      <Box
        sx={{
          position: "fixed",
          bottom: 80,
          right: 24,
          backgroundColor: TEXT_PRIMARY,
          color: "#fff",
          px: 2,
          py: 1,
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 1400,
        }}
      >
        <Typography sx={{ fontSize: 14, color: "#fff" }}>
          Copied to clipboard!
        </Typography>
      </Box>
    )}
    </>
  );
}

ThreatInsightDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  finding: PropTypes.object,
  initialSilenceMode: PropTypes.bool,
  onUpdated: PropTypes.func,
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
};

ThreatInsightDrawer.defaultProps = {
  finding: null,
  initialSilenceMode: false,
  onUpdated: undefined,
  onError: undefined,
  onSuccess: undefined,
};
